import { useModeAwareQuery } from './useModeAwareQuery';
import { useApi } from './useApi';
import { getGlobalDataStore } from '@/lib/mock-data/global-data-store';
import type { CollectionCase } from '@/types/collections';

// API Response type for collection cases by customer
interface CollectionCaseApiResponse {
  caseId: string;
  caseNumber: string;
  customerId: number;
  customerName: string;
  customerAccountNumber: string | null;
  priority: string;
  status: string;
  strategyId: number | null;
  strategyName: string | null;
  amount: number;
  daysInCollection: number;
  nextAction: string | null;
  nextActionDate: string | null;
  riskLevel: string;
  isPaused: boolean;
  assignedTo: number | null;
  assignedToName: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string | null;
  activityCount: number;
}

// The API returns an array directly, not wrapped in a data property
type GetCollectionCasesByCustomerResponse = CollectionCaseApiResponse[];

// Map API status to UI status
function mapApiStatusToUiStatus(status: string): CollectionCase['status'] {
  const statusMap: Record<string, CollectionCase['status']> = {
    'open': 'Active',
    'active': 'Active',
    'on_hold': 'On Hold',
    'on hold': 'On Hold',
    'closed': 'Closed',
    'promised': 'Promised',
    'escalated': 'Escalated',
  };
  return statusMap[status.toLowerCase()] ?? 'Active';
}

// Map API priority to UI priority
function mapApiPriorityToUi(priority: string): CollectionCase['priority'] {
  const priorityMap: Record<string, CollectionCase['priority']> = {
    'high': 'High',
    'medium': 'Medium',
    'low': 'Low',
  };
  return priorityMap[priority.toLowerCase()] ?? 'Medium';
}

// Map API risk level to UI risk level
function mapApiRiskLevelToUi(riskLevel: string): CollectionCase['riskLevel'] {
  const riskMap: Record<string, CollectionCase['riskLevel']> = {
    'high': 'High',
    'medium': 'Medium',
    'low': 'Low',
  };
  return riskMap[riskLevel.toLowerCase()] ?? 'Medium';
}

// Map API response to UI model
function mapCollectionCaseApiToUi(api: CollectionCaseApiResponse): CollectionCase {
  return {
    id: api.caseId,
    caseId: api.caseNumber,
    customerId: String(api.customerId),
    customerName: api.customerName,
    customerEmail: '',
    customerPhone: '',
    priority: mapApiPriorityToUi(api.priority),
    status: mapApiStatusToUiStatus(api.status),
    strategy: {
      id: api.strategyId ? String(api.strategyId) : '1',
      name: api.strategyName ?? 'Standard Collection',
      type: 'Standard',
    },
    strategyPhase: {
      currentPhase: 1,
      totalPhases: 4,
      phaseName: 'Initial Contact',
    },
    amount: api.amount,
    daysInCollection: api.daysInCollection,
    nextAction: api.nextAction ?? 'Review case',
    nextActionDate: api.nextActionDate ? new Date(api.nextActionDate) : new Date(),
    riskLevel: mapApiRiskLevelToUi(api.riskLevel),
    createdAt: new Date(api.createdAt),
    updatedAt: api.updatedAt ? new Date(api.updatedAt) : new Date(api.createdAt),
    isPaused: api.isPaused,
    assignedTo: api.assignedToName ?? undefined,
    notes: api.notes ?? undefined,
  };
}

/**
 * Hook to fetch collection cases for a specific customer
 * Uses API in Live mode, mock data in Demo mode
 */
export function useCustomerCollectionCases(customerId: string | undefined) {
  const { apiGet } = useApi();
  
  console.log('[useCustomerCollectionCases] Called with customerId:', customerId);
  
  // Get mock data for demo mode
  const getMockCases = (): CollectionCase[] => {
    if (!customerId) return [];
    const store = getGlobalDataStore();
    const allCases = store.getAllCases();
    return allCases.filter(x => x.customerId === customerId);
  };

  const { data, isLoading, error } = useModeAwareQuery<CollectionCase[], Error>(
    ['customer-collection-cases', customerId],
    async ({ signal }) => {
      console.log('[useCustomerCollectionCases] Fetching from API for customerId:', customerId);
      if (!customerId) return [];
      const response = await apiGet<GetCollectionCasesByCustomerResponse>(
        `/api/v1/collection-cases/customer/${customerId}`,
        undefined,
        signal
      );
      console.log('[useCustomerCollectionCases] API response:', response);
      // API returns array directly, not wrapped in data property
      return response.map(mapCollectionCaseApiToUi);
    },
    getMockCases(),
    {
      enabled: !!customerId,
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );

  return {
    cases: data ?? [],
    isLoading,
    error,
  };
}
